import type { ButtonHTMLAttributes } from "react";
import React from "react";
import { twMerge } from "tailwind-merge";
import { LoadingSpinner } from "./LoadingSpinner";

type ButtonVariant = "primary" | "secondary" | "black" | "error" | "icon" | "link";
type ButtonSize = "small" | "medium" | "large";

export type ButtonProps = {
  size?: ButtonSize;
  variant?: ButtonVariant;
  elevated?: boolean;
  loading?: boolean;
  useSpan?: boolean; // use this to avoid "Button cannot be descendant of Button" error
} & ButtonHTMLAttributes<HTMLButtonElement>;

const getSizeClasses = (size: ButtonSize): string => {
  switch (size) {
    case "small":
      return "text-sm font-bold";
    case "large":
      return "text-xl h-14 font-bold";
    case "medium":
    default:
      return "h-11 text-base font-bold";
  }
};

const getVariantClasses = (variant: ButtonVariant): string => {
  switch (variant) {
    case "secondary":
      return "text-gray-800 bg-transparent border-[1.5px] border-gray-800 hover:bg-gray-800 hover:border-gray-800 hover:text-white disabled:bg-transparent disabled:border-gray-800/20 disabled:font-weight-extrabold disabled:text-gray-800/50";
    case "black":
      return "text-white bg-black border border-gray-800";
    case "error":
      return "text-white bg-error-500 border border-error-500";
    case "icon":
      return "outline-none border-none w-6 h-6 p-0 m-0 flex items-center justify-center bg-text-gray-800/10";
    case "link":
      return "text-primary-100 font-area font-extrabold hover:text-primary-500 active:text-primary-600 visited:text-primary-500";
    case "primary":
    default:
      return "text-white bg-primary-100 border border-primary-100";
  }
};

const baseClasses =
  "w-full pt-2 pb-2 truncate px-8 font-area text-center rounded-full cursor-pointer motion-safe:transition-colors motion-safe:transition-shadow disabled:bg-gray-800/10 disabled:border-gray-800/10 disabled:text-gray-800/30 disabled:cursor-default disabled:shadow-none hover:outline-none hover:shadow-lg disabled:cursor-not-allowed disabled:shadow-none disabled:opacity-50 disabled:hover:shadow-none disabled:hover:opacity-50 aria-disabled:bg-gray-800/10 aria-disabled:border-gray-800/10 aria-disabled:text-gray-800/30 aria-disabled:cursor-not-allowed aria-disabled:shadow-none aria-disabled:opacity-50 aria-disabled:hover:shadow-none aria-disabled:hover:opacity-50";

const linkBaseClasses =
  "no-underline cursor-pointer motion-safe:transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:text-gray-800/30";

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "medium",
      variant = "primary",
      useSpan = false,
      elevated = false,
      className = "",
      children,
      loading,
      ...props
    },
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const Component = useSpan ? "span" : "button";
    const isDisabled = props.disabled || loading;
    const isLinkVariant = variant === "link";
    return (
      <Component // NOSONAR
        ref={ref}
        {...props}
        onClick={e => {
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
          }
          props.onClick?.(e);
        }}
        disabled={useSpan ? undefined : isDisabled}
        aria-disabled={isDisabled}
        role={useSpan ? "button" : undefined}
        tabIndex={0}
        className={twMerge(
          isLinkVariant ? linkBaseClasses : baseClasses,
          !isLinkVariant && getSizeClasses(size),
          getVariantClasses(variant),
          !isLinkVariant && elevated ? "shadow" : "",
          className,
        )}>
        <div className="relative flex h-full w-full items-center justify-center">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <LoadingSpinner sizeClasses="h-5 w-20" />
            </div>
          ) : null}
          <div className={loading ? "invisible" : ""}>{children}</div>
        </div>
      </Component>
    );
  },
);
Button.displayName = "Button";
